import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

interface Company {
  id: number;
  name: string;
  industry: string;
  description?: string;
  target_audience?: string;
  brand_voice?: string;
  created_at: string;
}

interface CompanyContextType {
  companies: Company[];
  selectedCompany: Company | null;
  loading: boolean;
  selectCompany: (company: Company) => void;
  addCompany: (company: Omit<Company, 'id' | 'created_at'>) => Promise<void>;
  updateCompany: (id: number, company: Partial<Company>) => Promise<void>;
  deleteCompany: (id: number) => Promise<void>;
  refreshCompanies: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, token } = useAuth();

  // Fetch companies when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      console.log('Auth state changed, fetching companies...');
      fetchCompanies();
    } else {
      console.log('Not authenticated, clearing companies');
      setCompanies([]);
      setSelectedCompany(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  const fetchCompanies = async () => {
    if (!isAuthenticated || !token) {
      console.log('Not authenticated, skipping company fetch');
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching companies...');
      
      const response = await axios.get('/api/companies');
      console.log('Companies response:', response.data);
      
      setCompanies(response.data);
      
      // Auto-select first company if none selected, or if selected company no longer exists
      if (response.data.length > 0) {
        if (!selectedCompany) {
          setSelectedCompany(response.data[0]);
          console.log('Auto-selected first company:', response.data[0]);
        } else {
          // Check if currently selected company still exists
          const companyStillExists = response.data.some((c: Company) => c.id === selectedCompany.id);
          if (!companyStillExists) {
            setSelectedCompany(response.data[0]);
            console.log('Previously selected company no longer exists, auto-selected:', response.data[0]);
          }
        }
      } else {
        setSelectedCompany(null);
      }
    } catch (error: any) {
      console.error('Failed to fetch companies:', error);
      
      // Clear companies on error
      setCompanies([]);
      setSelectedCompany(null);
    } finally {
      setLoading(false);
    }
  };

  const selectCompany = (company: Company) => {
    setSelectedCompany(company);
    console.log('Selected company:', company);
  };

  const addCompany = async (companyData: Omit<Company, 'id' | 'created_at'>) => {
    try {
      const response = await axios.post('/api/companies', companyData);
      const newCompany = response.data;
      
      setCompanies(prev => [newCompany, ...prev]);
      
      // Auto-select the new company
      setSelectedCompany(newCompany);
    } catch (error: any) {
      console.error('Failed to add company:', error);
      throw new Error(error.response?.data?.error || 'Failed to add company');
    }
  };

  const updateCompany = async (id: number, companyData: Partial<Company>) => {
    try {
      const response = await axios.put(`/api/companies/${id}`, companyData);
      const updatedCompany = response.data;
      
      setCompanies(prev => 
        prev.map(company => 
          company.id === id ? updatedCompany : company
        )
      );
      
      // Update selected company if it was the one being updated
      if (selectedCompany?.id === id) {
        setSelectedCompany(updatedCompany);
      }
    } catch (error: any) {
      console.error('Failed to update company:', error);
      throw new Error(error.response?.data?.error || 'Failed to update company');
    }
  };

  const deleteCompany = async (id: number) => {
    try {
      await axios.delete(`/api/companies/${id}`);
      
      setCompanies(prev => prev.filter(company => company.id !== id));
      
      // Clear selected company if it was deleted
      if (selectedCompany?.id === id) {
        const remainingCompanies = companies.filter(company => company.id !== id);
        setSelectedCompany(remainingCompanies.length > 0 ? remainingCompanies[0] : null);
      }
    } catch (error: any) {
      console.error('Failed to delete company:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete company');
    }
  };

  const refreshCompanies = async () => {
    await fetchCompanies();
  };

  const value: CompanyContextType = {
    companies,
    selectedCompany,
    loading,
    selectCompany,
    addCompany,
    updateCompany,
    deleteCompany,
    refreshCompanies,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

